import { z } from "zod";
import { CRON_COMMAND_STATUSES, CRON_COMMAND_TYPES } from "../types/repos.js";
export const agentSnapshotCronJobSchema = z.object({
    cron_job_id: z.string().regex(/^[0-9a-f]{64}$/, "expected sha256 hex"),
    raw_line: z.string().max(8 * 1024),
    schedule: z.string().min(1).max(256),
    command: z.string().min(1).max(8 * 1024),
    resolved_path: z.string().nullable(),
    repo_origin: z.string().nullable(),
    relative_path: z.string().nullable(),
    wrapped: z.boolean(),
    enabled: z.boolean(),
});
export const agentSnapshotSchema = z.object({
    agent_id: z.string().min(1).max(256),
    hostname: z.string().min(1).max(256),
    // Cap snapshot size — a saner-than-default upper bound, not a hard product limit.
    crontab: z.array(agentSnapshotCronJobSchema).max(500),
    // Reported by the host-agent at snapshot time; null when not installed.
    run_monitored_path: z.string().min(1).max(1024).nullable().optional(),
    // IANA timezone the host runs in (e.g. "Europe/Stockholm"). Optional so
    // pre-upgrade agents still validate.
    timezone: z.string().min(1).max(128).nullable().optional(),
});
export const agentCommandsQuerySchema = z.object({
    // Same bound as agentSnapshotSchema.agent_id — the agent identifies itself
    // here before the bearer token is verified against this row.
    agentId: z.string().min(1).max(256),
});
export const cronCommandAckSchema = z.object({
    status: z.enum(CRON_COMMAND_STATUSES.filter((s) => s !== "pending")),
    // Mirrored as ACK_ERROR_MAX in tools/host-agent/src/ack-error.ts, which
    // truncates to it; lowering this without that copy 400s long failure acks.
    error: z.string().max(2 * 1024).optional(),
});
export const cronJobIdParamSchema = z.object({
    cron_job_id: z.string().regex(/^[0-9a-f]{64}$/),
});
export const cronCommandIdParamSchema = z.object({
    command_id: z.string().min(1).max(128),
});
export const repoSlugParamSchema = z.object({
    slug: z
        .string()
        .min(1)
        .max(256)
        // owner/name (loose — GitHub also allows dots, hyphens, underscores).
        // Place `-` last in each character class so it's a literal, not a range.
        .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/),
});
// A repo-relative path for the spec-asset proxy. The SPA has already resolved
// `./` and `../`, so a `..` or empty segment (incl. a leading `/`) or a
// backslash means the path is trying to leave the repo.
const isRepoRelative = (p) => !p.includes("\\") && p.split("/").every((seg) => seg !== "" && seg !== "..");
export const repoAssetQuerySchema = z.object({
    path: z.string().min(1).max(1024).refine(isRepoRelative, "path must stay inside the repo"),
});
// The shape a crontab line's schedule can take: exactly five fields on one
// line, or a single `@macro`. cron-parser alone is not enough — it also
// accepts a six-field (seconds) expression and a four-field one, and the
// host-agent writes `${schedule} ${command}` verbatim, so a sixth field
// becomes the first word of the command (`*` glob-expands into a filename)
// and a missing one eats the command's first word as day-of-week. A newline
// would split the line in two. cron-parser still validates the field values.
export const crontabScheduleSchema = z
    .string()
    .trim()
    .min(1)
    .max(256)
    .regex(/^(?:@[a-z]+|[^\s]+(?:[ \t]+[^\s]+){4})$/, "expected five cron fields on one line, or an @macro");
// vixie-cron accepts eight `@macro` spellings. cron-parser@4.9.0 matches a
// macro by *exact key* against a `predefined` table holding only five of them
// (`lib/expression.js:48`, read at `:857`), so `@midnight`, `@annually` and
// `@reboot` miss it, fall through to ordinary five-field parsing, and die
// there — the "cannot resolve alias" text and its three-letter fragment come
// from the month/day-of-week alias substitution (`:163`), not from any macro
// resolution rule. Two of the three have an exact equivalent in that table;
// the third has no fire time at all.
const MACRO_EQUIVALENTS = {
    "@midnight": "@daily",
    "@annually": "@yearly",
};
/**
 * The expression to hand cron-parser for a schedule the crontab accepts, or
 * `null` when the schedule is valid but has no computable fire time
 * (`@reboot`, which fires at host boot). Callers must treat `null` as "accept,
 * nothing to compute" — not as a rejection. Anything else is returned as-is,
 * so an unknown `@macro` still fails cron-parser's own validation.
 */
export function cronParserExpression(schedule) {
    const trimmed = schedule.trim();
    if (trimmed === "@reboot")
        return null;
    return MACRO_EQUIVALENTS[trimmed] ?? trimmed;
}
export const setScheduleBodySchema = z.object({
    schedule: crontabScheduleSchema,
});
export const setWrappedBodySchema = z.object({
    // true → prepend `run-monitored -- ` to the command. false → strip it.
    wrapped: z.boolean(),
});
export const cronCommandTypeSchema = z.enum(CRON_COMMAND_TYPES);
//# sourceMappingURL=repos.js.map